// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, Button, InputGroup, Tabs, Tab, Classes, Intent } from '@blueprintjs/core';
import { useVendors } from '@/hooks/query/vendors';
import { useCreateVendor } from '@/hooks/query/vendors';
import { DialogFooter, DialogFooterActions } from '@/components';

export default function SupplierVendorMappingDialog({
  isOpen,
  supplierName,
  supplierId,
  onClose,
  onConfirm,
}) {
  const [tabId, setTabId] = useState('map');
  const [search, setSearch] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [page, setPage] = useState(1);
  const [quickVendorName, setQuickVendorName] = useState('');
  const [quickExternalVendorId, setQuickExternalVendorId] = useState('');
  const [quickExternalVendorName, setQuickExternalVendorName] = useState('');

  const { data, isLoading } = useVendors(
    { page, page_size: 20, search_keyword: search },
    { enabled: isOpen },
  );

  const { mutateAsync: createVendor, isLoading: isCreatingVendor } = useCreateVendor();

  const vendors = data?.vendors ?? [];
  const pagination = data?.pagination ?? {};
  const currentPage = pagination?.page ?? page;
  const pagesCount = pagination?.pagesCount ?? pagination?.pages_count ?? null;

  const options = useMemo(
    () =>
      vendors.map((v) => ({
        id: v?.id,
        label: v?.display_name ?? v?.displayName ?? `Vendor #${v?.id}`,
      })),
    [vendors],
  );

  const canConfirm = !!selectedVendorId;

  useEffect(() => {
    if (!isOpen) return;
    setTabId('map');
    setSearch('');
    setSelectedVendorId(null);
    setPage(1);
    setQuickVendorName(String(supplierName || '').trim());
    setQuickExternalVendorName(String(supplierName || '').trim());
    setQuickExternalVendorId(String(supplierId || '').trim());
  }, [isOpen, supplierId, supplierName]);

  const handleSave = async () => {
    if (tabId === 'quick-add') {
      const name = String(quickVendorName || '').trim();
      const externalId = String(quickExternalVendorId || '').trim();
      const externalName = String(quickExternalVendorName || '').trim();
      if (!name || !externalId) return;

      const res = await createVendor({
        display_name: name,
        active: true,
        first_name: name,
        company_name: name
      });
      const createdId = res?.data?.id ?? null;
      if (!createdId) return;

      return onConfirm({
        vendorId: createdId,
        externalVendorId: externalId,
        externalVendorName: externalName || null,
      });
    }

    if (!canConfirm) return;
    return onConfirm({ vendorId: selectedVendorId });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Map Supplier"
      canEscapeKeyClose
      canOutsideClickClose
      usePortal={false}
      enforceFocus={false}
      className="supplier-vendor-mapping-dialog"
    >
      <div className={Classes.DIALOG_BODY + ' supplier-vendor-mapping-dialog__body'}>
        <Tabs
          id="supplier-vendor-mapping-tabs"
          selectedTabId={tabId}
          onChange={(id) => setTabId(id)}
          className="supplier-vendor-mapping-dialog__tabs"
        >
          <Tab id="map" title="Map Vendor" className='vendor-mapping-tab'/>
          <Tab id="quick-add" title="Quick Add Vendor" className='vendor-mapping-tab'/>
        </Tabs>

        <div className="supplier-vendor-mapping-dialog__content">
          <div className="supplier-vendor-mapping-dialog__section-title">ASI Vendor:</div>
          <div className="supplier-vendor-mapping-dialog__grid-2">
            <div className="supplier-vendor-mapping-dialog__field">
              <div className="supplier-vendor-mapping-dialog__label">ASI Vendor Name</div>
              <div className="supplier-vendor-mapping-dialog__value">{supplierName || '—'}</div>
            </div>
            <div className="supplier-vendor-mapping-dialog__field">
              <div className="supplier-vendor-mapping-dialog__label">ASI Vendor #</div>
              <div className="supplier-vendor-mapping-dialog__value">{supplierId || '—'}</div>
            </div>
          </div>

          {tabId === 'quick-add' ? (
            <>
              <div className="supplier-vendor-mapping-dialog__grid-2 supplier-vendor-mapping-dialog__grid-2--inputs">
                <div className="supplier-vendor-mapping-dialog__field">
                  <div className="supplier-vendor-mapping-dialog__label-row">
                    <div className="supplier-vendor-mapping-dialog__label">Vendor Name</div>
                  
                  </div>
                  <InputGroup
                    placeholder="Vendor Name"
                    value={quickVendorName}
                    onChange={(e) => setQuickVendorName(e.target.value)}
                  />
                </div>
                <div className="supplier-vendor-mapping-dialog__field">
                  <div className="supplier-vendor-mapping-dialog__label-row">
                    <div className="supplier-vendor-mapping-dialog__label">Vendor #</div>
                    <button
                      type="button"
                      className="supplier-vendor-mapping-dialog__link"
                      onClick={() => setQuickExternalVendorId(String(supplierId || '').trim())}
                    >
                      Copy #
                    </button>
                  </div>
                  <InputGroup
                    placeholder="Vendor #"
                    value={quickExternalVendorId}
                    onChange={(e) => setQuickExternalVendorId(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'none' }}>
                {/* keep an explicit external name field for future; derived from supplierName by default */}
                <InputGroup
                  placeholder="External vendor name"
                  value={quickExternalVendorName}
                  onChange={(e) => setQuickExternalVendorName(e.target.value)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="supplier-vendor-mapping-dialog__section-title supplier-vendor-mapping-dialog__section-title--mt">
                Map To:
              </div>
              <div className="supplier-vendor-mapping-dialog__field">
                <div className="supplier-vendor-mapping-dialog__label">Vendor Name</div>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search vendor"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
              </div>

              <div className="supplier-vendor-mapping-dialog__list">
                {isLoading ? (
                  <div className="supplier-vendor-mapping-dialog__empty">Loading vendors…</div>
                ) : options.length === 0 ? (
                  <div className="supplier-vendor-mapping-dialog__empty">No vendors found.</div>
                ) : (
                  options.map((opt) => (
                    <label
                      key={opt.id}
                      className="supplier-vendor-mapping-dialog__list-row"
                    >
                      <input
                        type="radio"
                        name="vendor"
                        checked={String(selectedVendorId) === String(opt.id)}
                        onChange={() => setSelectedVendorId(opt.id)}
                      />
                      <span className="supplier-vendor-mapping-dialog__list-text">{opt.label}</span>
                    </label>
                  ))
                )}
              </div>

             
            </>
          )}
        </div>
      </div>
      <DialogFooter>
        <DialogFooterActions alignment={'right'}>
          <Button onClick={onClose} className="btn-close">
            Cancel
          </Button>
          <Button
       
            className="btn-submit"
            loading={tabId === 'quick-add' ? isCreatingVendor : false}
            disabled={
              tabId === 'quick-add'
                ? !String(quickVendorName || '').trim() || !String(quickExternalVendorId || '').trim()
                : !canConfirm
            }
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogFooterActions>
      </DialogFooter>
    </Dialog>
  );
}
